import React from 'react';
import { findPartRangeInMediaRule } from '../../utils/violationFixability';

interface FixabilityEvidenceBlockProps {
    /** Full 修正不可内容 from guideline (page domain). */
    mediaRuleDescription?: string;
    /** Stored excerpt to highlight within the full text. */
    highlightPart?: string;
    /** Allow drag-select in the full text to pick the excerpt (edit mode). */
    selectable?: boolean;
    onSelectPart?: (part: string) => void;
}

const HIGHLIGHT_MARK_STYLE: React.CSSProperties = {
    backgroundColor: '#ede9fe',
    color: '#5b21b6',
    padding: '0 2px',
    borderRadius: '2px',
};

/** Textarea-like affordance so it's clear this text can be drag-selected. */
const SELECTABLE_BOX_STYLE: React.CSSProperties = {
    border: '1px dashed #a78bfa',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    padding: '8px',
};

/**
 * Full guideline media rule text with unfixable_reason_description_part highlighted.
 */
const FixabilityEvidenceBlock: React.FC<FixabilityEvidenceBlockProps> = ({
    mediaRuleDescription,
    highlightPart,
    selectable = false,
    onSelectPart,
}) => {
    const fullText = mediaRuleDescription?.trim() ?? '';

    const handleMouseUp = () => {
        if (!selectable || !onSelectPart || !fullText) return;
        const selection = window.getSelection();
        const selected = selection?.toString().trim() ?? '';
        if (!selected || selected.length < 2) return;
        if (!fullText.includes(selected)) return;
        onSelectPart(selected);
        selection?.removeAllRanges();
    };

    if (!fullText) {
        return (
            <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                メディア会社の修正不可内容がガイドラインに登録されていません
            </p>
        );
    }

    const range = findPartRangeInMediaRule(fullText, highlightPart);

    if (!range) {
        const plainStyle: React.CSSProperties = {
            margin: 0,
            fontSize: '12px',
            color: '#4b5563',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            ...(selectable ? { cursor: 'text', userSelect: 'text' as const } : {}),
        };
        return (
            <div>
                <div style={selectable ? SELECTABLE_BOX_STYLE : undefined}>
                    <p
                        style={plainStyle}
                        onMouseUp={selectable ? handleMouseUp : undefined}
                    >
                        {fullText}
                    </p>
                </div>
                {highlightPart?.trim() && (
                    <p
                        style={{
                            margin: '8px 0 0',
                            fontSize: '11px',
                            color: '#6b7280',
                            fontStyle: 'italic',
                        }}
                    >
                        該当条文の位置を特定できませんでした
                    </p>
                )}
            </div>
        );
    }

    const before = fullText.slice(0, range.start);
    const highlighted = fullText.slice(range.start, range.end);
    const after = fullText.slice(range.end);

    const bodyStyle: React.CSSProperties = {
        margin: 0,
        fontSize: '12px',
        color: '#4b5563',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        ...(selectable
            ? { cursor: 'text', userSelect: 'text' as const }
            : {}),
    };

    return (
        <div style={selectable ? SELECTABLE_BOX_STYLE : undefined}>
            <p style={bodyStyle} onMouseUp={selectable ? handleMouseUp : undefined}>
                {before}
                <mark style={HIGHLIGHT_MARK_STYLE}>{highlighted}</mark>
                {after}
            </p>
        </div>
    );
};

export default FixabilityEvidenceBlock;
